// The dock container. Owns the DockviewApi, builds/restores one tabbed group
// per left/center/right zone (see PanelZone) of the active named preset
// (scene / art / systems / setup / code, switched via the top PresetSwitcher
// tab strip below), and resolves every legacy `/app/editor/:id/:mode` URL to
// "show the preset containing this panel, focused on it". Deliberately NOT
// free-form docking (see the roadmap's UX decision): each preset's zones are
// fixed, tabbed groups, not freely draggable, and switching tabs within a
// preset keeps every Route's own React state mounted (dockview portals
// hidden tab content rather than unmounting it - a real improvement over the
// old `{mode === 'x' && <XRoute/>}` switch, which fully remounted on every
// single mode change).
//
// Layout persistence and its defences against dockview's own
// fromJSON-corrupts-state bug (https://github.com/mathuo/dockview/issues/341)
// live in layoutStorage.ts / normalizeLayout.ts; read those first for the
// "why" behind this file's fromJSON-catch / resetLayout split.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DockviewReact, type AddPanelOptions, type DockviewApi, type DockviewReadyEvent, type SerializedDockview } from 'dockview-react';
import 'dockview-react/dist/styles/dockview.css';

import { createDebouncedSaver } from '../debouncedSave';
import { PanelHost } from './PanelHost';
import { PanelRegistryContext } from './PanelRegistryContext';
import { normalizeLayout } from './normalizeLayout';
import {
  clearPersistedWorkspaceState,
  loadPersistedWorkspaceState,
  savePersistedWorkspaceState,
  WORKSPACE_LAYOUT_SCHEMA_VERSION,
  type PersistedWorkspaceState,
} from './layoutStorage';
import { isDimensionValid, type Dimension, type PanelDescriptor, type PanelId, type PanelZone, type PresetDescriptor, type PresetId } from './types';

const PERSIST_DEBOUNCE_MS = 600;

// Left/right sidebar-style zones default to a narrower initial width than
// the center zone's remaining space - only applied to the first panel added
// to each zone's group, which is what sets that group's initial size.
const ZONE_ORDER: readonly PanelZone[] = ['left', 'center', 'right'];
const SIDEBAR_ZONE_WIDTH: Partial<Record<PanelZone, number>> = { left: 260, right: 260 };
/** Presets whose side zones need more than the default: Code's right zone
 * holds Output / Problems / Reference as one tab strip plus a four-way
 * output filter, which at 260px overflowed into dockview's tab dropdown. */
const PRESET_ZONE_WIDTH: Partial<Record<PresetId, Partial<Record<PanelZone, number>>>> = {
  code: { left: 270, right: 380 },
};
function sidebarZoneWidth(presetId: PresetId, zone: PanelZone): number | undefined {
  return PRESET_ZONE_WIDTH[presetId]?.[zone] ?? SIDEBAR_ZONE_WIDTH[zone];
}

export interface WorkspaceProps {
  projectId: string;
  projection: Dimension;
  /** Every rail-navigable panel (dimension-invalid entries are filtered
   * internally; Preview is intentionally not part of this list - see
   * screens-editor.tsx's ScreenEditor). */
  panels: readonly PanelDescriptor[];
  presets: readonly PresetDescriptor[];
  /** The current URL's mode segment, already legacy-alias-resolved and
   * dimension-guarded by the caller (ScreenEditor keeps its existing
   * `<Navigate>` redirects for a mode invalid in the current projection). */
  activePanelId: PanelId;
  /** Distraction-free mode (ScreenEditor's own shortcut/toggle): hides the
   * preset tab strip/Panels menu/reset buttons so the docked panels fill the
   * available height, the same "hide the surrounding chrome, keep the work"
   * affordance Godot/Unity/Unreal all converge on. Switching preset while
   * this is on isn't possible from here (no tab strip to click), which is
   * the intended trade-off - it's meant to be a temporary, easily-toggled
   * state, not a second permanent navigation mode. */
  hideChrome?: boolean;
  /** Fired whenever dockview's own active panel changes for any reason - a
   * preset-tab click, a Panels-menu reopen, or a plain in-preset tab click -
   * so the caller can keep the URL/top-bar-label/legacy-route-dependent
   * chrome in sync. Optional: GunsmithStudioPage's own nested Workspace has
   * no URL of its own to keep in sync and simply omits this. ScreenEditor
   * supplies it to call `navigate(..., {replace: true})`, restoring the
   * "URL always reflects what's on screen" property ModeRail's own `<Link>`s
   * used to provide before the top preset-tab strip replaced it (the tab
   * strip itself is deliberately not a `<Link>` - see PresetSwitcher's own
   * doc comment - so without this, nothing updated the URL at all). */
  onActivePanelChange?: (panelId: PanelId) => void;
  /** Hides just the "Reset all workspaces" control (default true - shown).
   * GunsmithStudioPage nests its own Workspace instance inside one of the
   * outer editor's own panels, which already has its own "Reset all
   * workspaces" button in its own top chrome for the outer preset set
   * (Scene/Art/Systems/Setup/Code) - a second, identically-labelled button
   * one level down was ambiguous (reset which workspace's layout?) and, with
   * Gunsmith's own `PRESETS` array being single-entry, redundant with its
   * still-shown "Reset layout" button (the two are equivalent when there is
   * only one preset to reset). */
  showResetAllWorkspaces?: boolean;
}

// Exported (here and below) purely so tests can exercise this preset/zone
// bucketing logic directly, the same way normalizeLayout.ts's pure functions
// are tested without mounting real dockview - see
// tests/unit/workspacePanels.test.ts.
export function panelIdsForPreset(panels: readonly PanelDescriptor[], presetId: PresetId, projection: Dimension): PanelId[] {
  return panels.filter((p) => p.presetId === presetId && isDimensionValid(p.dimension, projection)).map((p) => p.id);
}

/** Panels with no explicit `group` all share this one bucket per zone, so
 * they remain tab-siblings in a single dockview group - the pre-Phase-C
 * shape, and the shape every preset except Systems still wants (Scene's
 * center zone, for instance, must keep Edit/Map Builder/Environment Preview
 * as one tab strip, not three panels each crushed into its own third of the
 * viewport). Only a panel with a real, distinct `group` value gets split
 * into its own sub-group. */
const UNGROUPED = '__ungrouped';

/** `presetId`'s panels, grouped by PanelZone in ZONE_ORDER's left-to-right
 * order, and *within* each zone, further grouped into one sub-array per
 * distinct `group` (every panel with no `group` shares the single `UNGROUPED`
 * bucket, preserving the old "one flat tab strip per zone" behaviour for
 * presets that never set `group` at all) - see rebuildPreset, the sole
 * consumer, for how each sub-array becomes its own dockview group stacked
 * within the zone. Sub-array order follows first-encounter order in
 * `panels`, matching this function's pre-grouping behaviour. A zone with no
 * panels is simply absent from the result. */
export function panelIdsByZoneForPreset(panels: readonly PanelDescriptor[], presetId: PresetId, projection: Dimension): Partial<Record<PanelZone, PanelId[][]>> {
  const byZone: Partial<Record<PanelZone, Map<string, PanelId[]>>> = {};
  for (const p of panels) {
    if (p.presetId !== presetId || !isDimensionValid(p.dimension, projection)) continue;
    const zone = p.zone ?? 'center';
    const groupKey = p.group ?? UNGROUPED;
    const zoneGroups = (byZone[zone] ??= new Map());
    let ids = zoneGroups.get(groupKey);
    if (!ids) {
      ids = [];
      zoneGroups.set(groupKey, ids);
    }
    ids.push(p.id);
  }
  const result: Partial<Record<PanelZone, PanelId[][]>> = {};
  for (const zone of Object.keys(byZone) as PanelZone[]) {
    result[zone] = [...byZone[zone]!.values()];
  }
  return result;
}

/** Drops every id in `excludeIds` (the Panels menu's "closed" set) from each
 * zone's sub-groups - a pure, empty-input-safe transform so rebuildPreset's
 * excludeIds behaviour is testable without a live dockview instance. A
 * sub-group left with zero surviving ids stays in the result as an empty
 * array; rebuildPreset's addZoneGroup skips those rather than creating an
 * empty dockview group. */
export function withoutClosedIds(
  byZone: Partial<Record<PanelZone, PanelId[][]>>,
  excludeIds: ReadonlySet<PanelId>,
): Partial<Record<PanelZone, PanelId[][]>> {
  if (excludeIds.size === 0) return byZone;
  const result: Partial<Record<PanelZone, PanelId[][]>> = {};
  for (const zone of Object.keys(byZone) as PanelZone[]) {
    const groups = byZone[zone];
    if (groups) result[zone] = groups.map((ids) => ids.filter((id) => !excludeIds.has(id)));
  }
  return result;
}

/** Picks a panel to focus when the one a URL/rail link actually wants has
 * been closed (dockview no longer holds it) - the orphaned-tab bug's fix.
 * `isOpen` is injected (rather than reading a live dockview api directly) so
 * this selection logic is testable with a plain fake. Returns undefined when
 * nothing in the preset is open (an empty preset), in which case there is
 * nothing to fall back to. */
export function pickFallbackPanelId(ids: readonly PanelId[], isOpen: (id: PanelId) => boolean, avoid: PanelId): PanelId | undefined {
  return ids.find((id) => id !== avoid && isOpen(id));
}

export function Workspace({ projectId, projection, panels, presets, activePanelId, hideChrome, onActivePanelChange, showResetAllWorkspaces = true }: WorkspaceProps) {
  // Stashed in a ref so the dockview event listener (attached once per
  // instance in onReady, see below) always calls the *current* callback
  // rather than whichever one was passed in at attach time.
  const onActivePanelChangeRef = useRef(onActivePanelChange);
  onActivePanelChangeRef.current = onActivePanelChange;
  const registry = useMemo(() => new Map(panels.map((p) => [p.id, p] as const)), [panels]);
  const panelById = useCallback((id: PanelId) => registry.get(id), [registry]);
  const defaultPresetId = presets[0]?.id as PresetId;

  const apiRef = useRef<DockviewApi | null>(null);
  const [instanceKey, setInstanceKey] = useState(0);
  const [activePresetId, setActivePresetIdState] = useState<PresetId>(defaultPresetId);
  // Panel ids deliberately closed (via a dockview tab's own close button, or
  // the Panels menu below) within the *current* preset's live dockview
  // instance. Kept in sync live via onDidAddPanel/onDidRemovePanel (wired in
  // onReady) rather than only updated by our own menu, so a tab closed the
  // native dockview way is tracked identically. Always reset to whatever
  // `rebuildPreset` was last called with (empty for every call site except
  // the Panels menu's own reopen path below) - see rebuildPreset's own
  // comment for why a full preset rebuild is the reset boundary for this.
  const [closedPanelIds, setClosedPanelIds] = useState<ReadonlySet<PanelId>>(new Set());
  // True for the duration of rebuildPreset's own api.clear()/addPanel calls
  // - see rebuildPreset for why the onDidRemovePanel/onDidAddPanel listeners
  // below must ignore churn while this is set.
  const isRebuildingRef = useRef(false);
  // Mirrors `activePresetId` synchronously for the dockview event listeners
  // below, which are attached once per instance (see onReady) and must
  // always resolve against whichever preset is current, not whichever was
  // current when they were attached.
  const activePresetIdRef = useRef(activePresetId);
  const setActivePreset = useCallback((id: PresetId) => {
    activePresetIdRef.current = id;
    setActivePresetIdState(id);
  }, []);

  // Last-active tab per preset, kept in sync with dockview's own active-panel
  // events so switching presets and back resumes where the creator left it.
  const activeTabByPresetRef = useRef<Partial<Record<PresetId, PanelId>>>({});
  // onReady's own dockview event subscriptions - torn down explicitly (see
  // the unmount effect below and onReady itself) since DockviewReact does
  // not call a value returned from `onReady` for cleanup.
  const disposablesRef = useRef<{ dispose(): void }[]>([]);

  // Bumped by resetLayout so an already-in-flight debounced save (there is
  // no cancel(), only schedule()/flush()) discards itself instead of
  // re-writing a stale layout to storage a few hundred ms after the reset.
  const generationRef = useRef(0);
  const saverRef = useRef(
    createDebouncedSaver<{ generation: number; state: PersistedWorkspaceState }>((payload) => {
      if (payload.generation !== generationRef.current) return;
      savePersistedWorkspaceState(projectId, payload.state);
    }, PERSIST_DEBOUNCE_MS),
  );

  const schedulePersist = useCallback(
    (presetId: PresetId, layout: SerializedDockview) => {
      saverRef.current.schedule({
        generation: generationRef.current,
        state: {
          schemaVersion: WORKSPACE_LAYOUT_SCHEMA_VERSION,
          activePresetId: presetId,
          activeTabByPreset: activeTabByPresetRef.current,
          layout,
        },
      });
    },
    [],
  );

  useEffect(() => {
    const saver = saverRef.current;
    const onPageHide = () => saver.flush(true);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      saver.flush();
    };
  }, []);

  /** Replaces the workspace's panel set with `presetId`'s panels (fresh
   * `addPanel` calls, never `fromJSON`) and focuses `activeId`. Safe to call
   * on a healthy instance at any time - unlike the fromJSON-failed recovery
   * path below, this never touches `api.clear()` on data we don't trust.
   *
   * One dockview group per non-empty PanelZone, left-to-right, each panel
   * tabbed within its own zone's group - roadmap.md section 3.2's decision
   * ("build fixed dock zones... with tabbed panel groups, not free-form
   * docking"). Splitting a fully-decomposed preset (e.g. Systems, which
   * carries every panel from Logic/HUD/Audio/FX/Game Data/Cutscenes/Intro
   * Sequences at once) into three zones instead of one flat tab group keeps
   * each zone's tab strip to the handful of panels that actually belong
   * together, rather than a single row of 15+ tabs mostly hidden behind
   * dockview's own overflow menu.
   *
   * Center is built first, as the grid's sole (root) group - a group added
   * with no `position` fills 100% of the grid, since there's nothing yet to
   * size it against; `initialWidth` on that first call is a no-op for
   * exactly that reason. Left and right are then split OFF the center
   * group, each requesting its own fixed `initialWidth` - the width has to
   * be requested by whichever group is splitting away, not by the one it's
   * splitting away from. Getting this backwards (sizing the first-added
   * zone instead of the zones split off it) starves the center zone: a
   * left zone added first with no sibling to split against silently claims
   * the *entire* grid, and only loses width to whatever splits off it
   * later - which is exactly backwards for a zone meant to be a narrow
   * fixed sidebar. This matters beyond cosmetics: Edit and Map Builder
   * default to 'center' and own a real, non-decomposed multi-column layout
   * of their own (scene tree | viewport | inspector) that needs the
   * center zone's actual remaining width, not whatever's left after a
   * mis-sized sidebar ate most of the grid. */
  const rebuildPreset = useCallback(
    (api: DockviewApi, presetId: PresetId, activeId?: PanelId, excludeIds: ReadonlySet<PanelId> = new Set()) => {
      const byZone = withoutClosedIds(panelIdsByZoneForPreset(panels, presetId, projection), excludeIds);
      const allIds = ZONE_ORDER.flatMap((zone) => (byZone[zone] ?? []).flat());
      // Every call site except the Panels menu's own reopen path passes no
      // excludeIds, so this is the point where "closed panels" resets to
      // none - a preset switch or an explicit reset has always thrown away
      // in-session closes (rebuilding fresh from the registry every time),
      // this just makes that pre-existing behaviour explicit in the state
      // the Panels menu reads.
      setClosedPanelIds(excludeIds);
      // api.clear() below fires onDidRemovePanel for every panel the
      // *previous* preset (or the previous excludeIds) had open, and every
      // addPanel call below fires onDidAddPanel - both wired (see onReady)
      // to keep closedPanelIds in sync with genuine ad hoc tab closes (a
      // native dockview tab-close, or the Panels menu). Without this guard,
      // a plain preset switch would misreport the *outgoing* preset's own
      // panels as "closed" in the incoming preset's Panels menu - they were
      // never closed, they simply aren't part of this preset.
      // isRebuildingRef suppresses those listeners for exactly this
      // rebuild's own churn; setClosedPanelIds above is already the correct,
      // deliberate final state for this call.
      isRebuildingRef.current = true;
      try {
        api.clear();
        if (allIds.length === 0) return;
        const resolvedActiveId = activeId && allIds.includes(activeId) ? activeId : allIds[0];
        if (!resolvedActiveId) return; // unreachable (allIds is non-empty here), satisfies noUncheckedIndexedAccess

        // idGroups is one sub-array per named `group` within this zone (see
        // panelIdsByZoneForPreset) - the first sub-array is positioned
        // relative to the *other zones* exactly as before Phase C (via
        // `position`), and every subsequent sub-array in this same zone is
        // stacked `below` the zone's own first sub-group instead, so e.g.
        // Systems' left zone shows Logic/HUD/Audio/Data as visually distinct,
        // separately-tabbed stacked groups rather than one flat tab row.
        const addZoneGroup = (idGroups: PanelId[][] | undefined, position?: (firstGroupId: string) => AddPanelOptions['position'], initialWidth?: number) => {
          if (!idGroups) return undefined;
          let zoneAnchorGroupId: string | undefined;
          for (const ids of idGroups) {
            if (ids.length === 0) continue;
            let firstInSubGroupId: string | undefined;
            for (const id of ids) {
              const descriptor = panelById(id);
              if (!descriptor) continue;
              const panel = api.addPanel({
                id,
                component: 'panel-host',
                title: descriptor.title,
                params: { panelId: id },
                inactive: true,
                position: firstInSubGroupId
                  ? { referenceGroup: firstInSubGroupId, direction: 'within' }
                  : zoneAnchorGroupId
                    ? { referenceGroup: zoneAnchorGroupId, direction: 'below' }
                    : position?.(zoneAnchorGroupId ?? ''),
                initialWidth: firstInSubGroupId || zoneAnchorGroupId ? undefined : initialWidth,
              });
              firstInSubGroupId ??= panel.group.id;
            }
            zoneAnchorGroupId ??= firstInSubGroupId;
          }
          return zoneAnchorGroupId;
        };

        // Center first, as the anchor everything else splits away from.
        let anchorGroupId = addZoneGroup(byZone.center, undefined, undefined);
        // Left and right split off the anchor, each with its own fixed
        // width. If center was empty, the first side zone processed becomes
        // the anchor for the other instead (defensive - no current preset
        // has this shape, but a future one might).
        const leftGroupId = addZoneGroup(
          byZone.left,
          anchorGroupId ? () => ({ referenceGroup: anchorGroupId!, direction: 'left' }) : undefined,
          sidebarZoneWidth(presetId, 'left'),
        );
        anchorGroupId ??= leftGroupId;
        addZoneGroup(
          byZone.right,
          anchorGroupId ? () => ({ referenceGroup: anchorGroupId!, direction: 'right' }) : undefined,
          sidebarZoneWidth(presetId, 'right'),
        );

        api.getPanel(resolvedActiveId)?.api.setActive();
      } finally {
        isRebuildingRef.current = false;
      }
    },
    [panels, projection, panelById],
  );

  const switchPreset = useCallback(
    (nextPresetId: PresetId, focusId?: PanelId) => {
      const api = apiRef.current;
      if (!api) return;
      // Snapshot the outgoing preset's own tab order/state before replacing
      // it, so switching back later can restore it via normalizeLayout.
      schedulePersist(activePresetIdRef.current, api.toJSON());
      const ids = panelIdsForPreset(panels, nextPresetId, projection);
      const remembered = activeTabByPresetRef.current[nextPresetId];
      const desired = focusId ?? (remembered && ids.includes(remembered) ? remembered : ids[0]);
      rebuildPreset(api, nextPresetId, desired);
      setActivePreset(nextPresetId);
    },
    [panels, projection, schedulePersist, rebuildPreset, setActivePreset],
  );

  const onReady = useCallback(
    (event: DockviewReadyEvent) => {
      const api = event.api;
      apiRef.current = api;

      const persisted = loadPersistedWorkspaceState(projectId);
      const urlTarget = panelById(activePanelId);
      const urlValid = Boolean(urlTarget?.presetId && isDimensionValid(urlTarget.dimension, projection));
      const initialPresetId: PresetId = urlValid
        ? (urlTarget!.presetId as PresetId)
        : persisted && presets.some((p) => p.id === persisted.activePresetId)
          ? persisted.activePresetId
          : defaultPresetId;

      if (persisted) activeTabByPresetRef.current = persisted.activeTabByPreset;

      const validIds = new Set(panelIdsForPreset(panels, initialPresetId, projection));
      let restored = false;
      if (persisted && persisted.activePresetId === initialPresetId) {
        const normalized = normalizeLayout(persisted.layout, validIds);
        if (normalized) {
          try {
            api.fromJSON(normalized);
            restored = true;
            // A panel closed before the reload that persisted this layout
            // is simply absent from `normalized` - normalizeLayout only
            // prunes ids that are no longer valid, but a deliberately closed
            // id is still valid (still in the registry), just not present in
            // the saved panels dict/tree. Diff the registry's valid ids for
            // this preset against what fromJSON actually restored so the
            // Panels menu's checkmarks are correct immediately after a
            // reload, not just for closes that happen in the current tab.
            setClosedPanelIds(new Set([...validIds].filter((id) => !api.getPanel(id))));
          } catch (err) {
            // fromJSON left the grid mid-mutated (dockview/#341) - do not
            // call api.clear() to recover (that's the documented cascading
            // failure). Remount DockviewReact under a fresh key instead: a
            // brand new dockview-core instance, guaranteed unpoisoned. The
            // corrupt persisted layout is dropped so the remount doesn't
            // just try (and fail) the same fromJSON call again.
            console.error('[panel-system] fromJSON failed on a normalized layout; remounting the workspace.', err);
            clearPersistedWorkspaceState(projectId);
            setInstanceKey((k) => k + 1);
            return;
          }
        }
      }

      const remembered = activeTabByPresetRef.current[initialPresetId];
      const fallbackActiveId = remembered && validIds.has(remembered) ? remembered : [...validIds][0];
      const desiredActiveId = urlValid ? activePanelId : fallbackActiveId;

      if (restored) {
        // The panel this reload wants may itself have been closed before
        // the page was last left (normalizeLayout only prunes ids that are
        // no longer *valid*, not ones a creator deliberately closed, so a
        // closed-but-still-registered panel id survives into the restored
        // layout as simply absent) - without this fallback, a reload
        // landing on a closed panel silently activated nothing, the same
        // orphaned-tab dead end the Panels menu exists to fix, surviving a
        // refresh.
        const target = desiredActiveId && api.getPanel(desiredActiveId) ? desiredActiveId : pickFallbackPanelId([...validIds], (id) => Boolean(api.getPanel(id)), desiredActiveId ?? '');
        if (target) api.getPanel(target)?.api.setActive();
      } else {
        rebuildPreset(api, initialPresetId, desiredActiveId);
      }

      setActivePreset(initialPresetId);

      // Attached exactly once per dockview instance (onReady fires once per
      // DockviewReact mount; `key={instanceKey}` is what creates a new one).
      // `onReady`'s own return value isn't used for cleanup by DockviewReact
      // - these disposables are tracked in `disposablesRef` and torn down
      // explicitly, see the unmount effect below. Listeners read
      // `activePresetIdRef` at event time rather than closing over
      // `initialPresetId`, since a later switchPreset() changes which
      // preset these events describe without recreating the instance.
      disposablesRef.current.forEach((d) => d.dispose());
      disposablesRef.current = [
        api.onDidActivePanelChange((changeEvent) => {
          const presetId = activePresetIdRef.current;
          activeTabByPresetRef.current = { ...activeTabByPresetRef.current, [presetId]: changeEvent.panel?.id };
          if (changeEvent.panel?.id) onActivePanelChangeRef.current?.(changeEvent.panel.id);
        }),
        api.onDidLayoutChange(() => schedulePersist(activePresetIdRef.current, api.toJSON())),
        // Tracks the Panels menu's "closed" checkmarks live against dockview's
        // own ground truth, so a tab closed via its native close button (not
        // just via the menu below) shows up there too - this pair is the fix
        // for the orphaned-tab bug: closing a panel used to be a one-way
        // trip, recoverable only via a full "Reset all workspaces". Ignored
        // entirely while isRebuildingRef is set - that's rebuildPreset's own
        // clear()/addPanel churn, not a genuine ad hoc close, and it already
        // sets closedPanelIds to the correct final value itself.
        api.onDidRemovePanel((panel) => {
          if (isRebuildingRef.current) return;
          setClosedPanelIds((prev) => (prev.has(panel.id) ? prev : new Set(prev).add(panel.id)));
        }),
        api.onDidAddPanel((panel) => {
          if (isRebuildingRef.current) return;
          setClosedPanelIds((prev) => {
            if (!prev.has(panel.id)) return prev;
            const next = new Set(prev);
            next.delete(panel.id);
            return next;
          });
        }),
      ];
    },
    [projectId, panels, presets, projection, activePanelId, panelById, defaultPresetId, rebuildPreset, setActivePreset, schedulePersist],
  );

  useEffect(() => {
    return () => disposablesRef.current.forEach((d) => d.dispose());
  }, []);

  // URL is the source of truth after the initial mount: a rail click or a
  // bookmarked/legacy `/mode` URL always wins over whatever tab was last
  // active, even across a preset switch.
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return; // onReady already resolved the initial panel/preset.
    }
    const api = apiRef.current;
    const target = panelById(activePanelId);
    if (!api || !target || !target.presetId || !isDimensionValid(target.dimension, projection)) return;
    if (target.presetId !== activePresetIdRef.current) {
      switchPreset(target.presetId, activePanelId);
    } else {
      const panel = api.getPanel(activePanelId);
      if (panel) {
        panel.api.setActive();
      } else {
        // The panel this URL/rail link wants was closed (dockview no longer
        // holds it) - previously this branch silently did nothing, the
        // orphaned-tab bug. Falling back to whatever else is still open in
        // this preset keeps the click from being a dead end; the Panels
        // menu (PresetSwitcher) is the actual way to bring the specific
        // closed panel back.
        const ids = panelIdsForPreset(panels, activePresetIdRef.current, projection);
        const fallbackId = pickFallbackPanelId(ids, (id) => Boolean(api.getPanel(id)), activePanelId);
        if (fallbackId) api.getPanel(fallbackId)?.api.setActive();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally URL-driven only; panelById/projection/switchPreset read live refs/closures and re-running this on their own change (rather than only on a URL change) would fight a manual preset switch.
  }, [activePanelId]);

  /** Toggles one panel's open/closed state within the active preset. Closing
   * is a plain dockview close (its own onDidRemovePanel listener above keeps
   * closedPanelIds in sync). Reopening re-runs rebuildPreset excluding every
   * *other* still-closed id, rather than a bespoke single-panel insertion -
   * reuses the same, already-covered zone-building code path instead of
   * duplicating its left/center/right positioning logic for one panel. */
  const togglePanelOpen = useCallback(
    (id: PanelId) => {
      const api = apiRef.current;
      if (!api) return;
      const panel = api.getPanel(id);
      if (panel) {
        panel.api.close();
        return;
      }
      const nextClosed = new Set(closedPanelIds);
      nextClosed.delete(id);
      rebuildPreset(api, activePresetIdRef.current, id, nextClosed);
    },
    [closedPanelIds, rebuildPreset],
  );

  /** Resets only the preset currently on screen - the everyday recovery for
   * "I closed a tab and want it back," or "I've made a mess of this one
   * workspace." Re-running rebuildPreset on the live api (no remount) is
   * enough: the existing onDidLayoutChange listener persists the fresh
   * result the same way any other layout mutation already does, so there is
   * no separate per-preset persisted-state shape to maintain. */
  /** The old global "Reset layout": wipes every preset's persisted state and
   * remounts dockview fresh, landing back on the default preset regardless
   * of which one was active. Kept as a distinct, clearly-labelled action for
   * genuine corruption (e.g. the fromJSON-failure recovery path above), not
   * the first thing reached for over one closed tab. */
  const resetAllWorkspaces = useCallback(() => {
    // Invalidate any in-flight debounced save first - it would otherwise
    // land ~PERSIST_DEBOUNCE_MS after this clear and resurrect the layout
    // being reset.
    generationRef.current += 1;
    clearPersistedWorkspaceState(projectId);
    activeTabByPresetRef.current = {};
    apiRef.current = null;
    setActivePreset(defaultPresetId);
    // Remounting DockviewReact under a fresh key - rather than reusing the
    // live api - is what makes this work unconditionally, even right after
    // a fromJSON failure has left the current instance's internal grid state
    // broken (see onReady's catch block above).
    setInstanceKey((k) => k + 1);
  }, [projectId, defaultPresetId, setActivePreset]);

  const resetActivePreset = useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    const activeId = activePresetIdRef.current;
    const remembered = activeTabByPresetRef.current[activeId];
    try {
      rebuildPreset(api, activeId, remembered);
    } catch (err) {
      // rebuildPreset calls api.clear() on the *live* instance - if that
      // instance's internal grid state is already broken (the same
      // dockview/#341-shaped corruption the fromJSON-failure path above
      // guards against, just reached a different way), clearing it can
      // throw instead of recovering. Falling back to the full remount here
      // is what keeps "Reset layout" a genuine, unconditional recovery
      // action rather than one that only works on an already-healthy
      // instance - the property the old single resetLayout had before it
      // was split into a per-preset and an all-presets action.
      console.error('[panel-system] resetActivePreset failed on the live instance; falling back to a full reset.', err);
      resetAllWorkspaces();
    }
  }, [rebuildPreset, resetAllWorkspaces]);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
      {!hideChrome && <PresetSwitcher
        presets={presets}
        activePresetId={activePresetId}
        panels={panels}
        projection={projection}
        closedPanelIds={closedPanelIds}
        onSelectPreset={(id) => (id !== activePresetIdRef.current ? switchPreset(id) : undefined)}
        onTogglePanel={togglePanelOpen}
        onResetPreset={resetActivePreset}
        onResetAll={resetAllWorkspaces}
        showResetAllWorkspaces={showResetAllWorkspaces}
      />}
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <PanelRegistryContext.Provider value={registry}>
          <DockviewReact
            key={instanceKey}
            className="dockview-theme-abyss ss-workspace-dockview"
            components={{ 'panel-host': PanelHost }}
            onReady={onReady}
          />
        </PanelRegistryContext.Provider>
      </div>
    </div>
  );
}

/** Now the sole top-level workspace switcher (ModeRail, the old persistent
 * vertical icon rail, is gone - see screens-editor.tsx). Each preset is a
 * real clickable tab; clicking calls onSelectPreset with no specific panel
 * in mind, which resumes wherever the creator last left that preset
 * (switchPreset's own remembered-tab fallback) rather than resetting to a
 * fixed default every time - the same "come back where you left off" feel
 * Blender/Godot's own workspace tabs have. Deliberately not URL-driven
 * (no <Link>): switchPreset's URL-driven caller always supplies an explicit
 * focusId, which would override the remembered tab every time and defeat
 * the point. A legacy `/app/editor/:id/:mode` URL still lands correctly on
 * mount or on direct navigation (unchanged, see the activePanelId effect
 * above); once inside the workspace, fine-grained panel navigation happens
 * via the tab strip below and the Panels menu here, not this row. */
function PresetSwitcher({
  presets,
  activePresetId,
  panels,
  projection,
  closedPanelIds,
  onSelectPreset,
  onTogglePanel,
  onResetPreset,
  onResetAll,
  showResetAllWorkspaces,
}: {
  presets: readonly PresetDescriptor[];
  activePresetId: PresetId;
  panels: readonly PanelDescriptor[];
  projection: Dimension;
  closedPanelIds: ReadonlySet<PanelId>;
  onSelectPreset: (id: PresetId) => void;
  onTogglePanel: (id: PanelId) => void;
  onResetPreset: () => void;
  onResetAll: () => void;
  showResetAllWorkspaces: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        height: 40,
        flexShrink: 0,
        padding: '0 10px',
        background: 'var(--ss-bg-2)',
        borderBottom: '1px solid var(--ss-border-soft)',
        gap: 4,
      }}
    >
      {presets.map((p) => {
        const active = p.id === activePresetId;
        return (
          <button
            key={p.id}
            onClick={() => onSelectPreset(p.id)}
            aria-current={active ? 'true' : undefined}
            style={{
              height: 28,
              padding: '0 14px',
              borderRadius: 8,
              border: '1px solid ' + (active ? 'var(--ss-accent-border)' : 'transparent'),
              background: active ? 'var(--ss-accent-soft)' : 'transparent',
              color: active ? 'var(--ss-accent)' : 'var(--ss-text-2)',
              fontFamily: 'var(--ss-sans)',
              fontSize: 12.5,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            {p.label}
          </button>
        );
      })}
      <div style={{ flex: 1 }} />
      <PanelsMenu activePresetId={activePresetId} panels={panels} projection={projection} closedPanelIds={closedPanelIds} onTogglePanel={onTogglePanel} />
      <button
        onClick={onResetPreset}
        className="ss-btn ss-btn--ghost ss-btn--sm"
        title="Rebuild this workspace's panel layout back to its default (undoes closed/moved/resized tabs here only)"
        style={{ alignSelf: 'center' }}
      >
        Reset layout
      </button>
      {showResetAllWorkspaces && (
        <button
          onClick={() => {
            if (window.confirm('Reset every workspace layout for this project back to its default? This clears customisation in Scene, Art, Systems, Setup and Code, not just the one you’re looking at.')) {
              onResetAll();
            }
          }}
          className="ss-btn ss-btn--ghost ss-btn--sm"
          title="Reset every preset's panel layout for this project, not just the one on screen"
          style={{ alignSelf: 'center', color: 'var(--ss-text-3)' }}
        >
          Reset all workspaces
        </button>
      )}
    </div>
  );
}

/** The non-destructive "bring a closed panel back" affordance (the gap the
 * orphaned-tab bug exposed): lists every panel valid for the active preset
 * with an open/closed toggle, decoupled from whichever tab strip a panel was
 * closed from - the same role Unity/Unreal/Roblox's own "Window" menu plays
 * for their free-form docking. */
function PanelsMenu({
  activePresetId,
  panels,
  projection,
  closedPanelIds,
  onTogglePanel,
}: {
  activePresetId: PresetId;
  panels: readonly PanelDescriptor[];
  projection: Dimension;
  closedPanelIds: ReadonlySet<PanelId>;
  onTogglePanel: (id: PanelId) => void;
}) {
  const [open, setOpen] = useState(false);
  const entries = panels.filter((p) => p.presetId === activePresetId && isDimensionValid(p.dimension, projection));
  return (
    <div style={{ position: 'relative', alignSelf: 'center' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="ss-btn ss-btn--ghost ss-btn--sm"
        title="Show or reopen this workspace's panels"
        aria-expanded={open}
      >
        Panels{closedPanelIds.size > 0 ? ` (${closedPanelIds.size} hidden)` : ''}
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 74 }} onClick={() => setOpen(false)} />
          <div
            role="menu"
            style={{
              position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 75, minWidth: 200,
              background: 'var(--ss-bg-2)', border: '1px solid var(--ss-border-soft)', borderRadius: 10,
              boxShadow: '0 12px 32px rgba(0,0,0,0.28)', padding: 6, display: 'flex', flexDirection: 'column', gap: 1,
              maxHeight: 320, overflowY: 'auto',
            }}
          >
            {entries.map((p) => {
              const isClosed = closedPanelIds.has(p.id);
              return (
                <button
                  key={p.id}
                  role="menuitemcheckbox"
                  aria-checked={!isClosed}
                  onClick={() => onTogglePanel(p.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6,
                    border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer',
                    fontSize: 12, fontFamily: 'var(--ss-sans)', color: isClosed ? 'var(--ss-text-3)' : 'var(--ss-text)',
                  }}
                >
                  <span style={{ width: 13, textAlign: 'center', color: 'var(--ss-accent)' }}>{isClosed ? '' : '✓'}</span>
                  {p.title}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
